import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import cn from 'classnames';

import ViewportMgr from 'coreClasses/view/ViewportMgr';
import {useOnClickOutside, useTransitionEvent} from 'coreHooks';
import {getCoords} from 'coreUtils/dom';

import DropdownContext from './contexts/DropdownContext';
import {IDropdownContext} from './contexts/IDropdownContext';
import {EDropdownDirection, IDropdownProps} from './IDropdown';
import {DropdownMenu, DropdownTrigger} from './Subcomponents';

import styles from './Dropdown.module.scss';

const Dropdown = ({value, direction = EDropdownDirection.TOP, disabled, children}: IDropdownProps) => {
	const [showMenu, setShowMenu] = useState(false);
	const [transitionEnded, setTransitionEnded] = useState(true);
	const [menuDirection, setMenuDirection] = useState('');
	const [menuCoords, setMenuCoords] = useState<IDropdownContext['menuCoords']>(null);
	const menuRef = useRef<HTMLDivElement>();

	useTransitionEvent('transitionstart', menuRef, () => showMenu && setTransitionEnded(false));
	useTransitionEvent('transitionend', menuRef, () => !showMenu && setTransitionEnded(true));

	const resetMenuCoords = useCallback(() => {
		setMenuCoords(null);
	}, []);

	const handleClickOutside = useCallback(() => {
		setShowMenu(false);
	}, []);

	const dropdownRefs = useOnClickOutside<Record<string, HTMLDivElement>>(handleClickOutside);

	const handleShowMenu = useCallback((isShow: boolean) => {
		setShowMenu(() => isShow);

		if (isShow) {
			setMenuCoords(() => {
				let {top, left, bottom, y} = getCoords(dropdownRefs.current.triggerEl);

				if (direction === EDropdownDirection.BOTTOM) {
					top = bottom;
				}

				if (direction === EDropdownDirection.DYNAMIC) {
					const headerHeight = ViewportMgr.getHeaderHeightTotal();

					if (((window.innerHeight / 2) + headerHeight) > y ) {
						top = bottom;
						setMenuDirection(`dropdown__${EDropdownDirection.BOTTOM}-direction`);
					} else {
						setMenuDirection(`dropdown__${EDropdownDirection.TOP}-direction`);
					}
				}

				const newCoords = {top, left, triggerHeight: dropdownRefs.current.triggerEl.offsetHeight};

				return newCoords;
			});
		}
	}, []);

	const setMenuRef = useCallback((el: HTMLDivElement) => {
		menuRef.current = el;
	}, []);

	const handleRefSet = (el: HTMLDivElement) => {
		if (!el) {
			return;
		}

		// useOnClickOutside hook needs this
		dropdownRefs.current = {
			triggerEl: el,
			menuEl: menuRef.current,
		};
	};

	const providerValue = useMemo(
		() => ({
			value,
			showMenu,
			menuCoords,
			menuDirection,
			handleShowMenu,
			setMenuRef,
			disabled
		}),
		[disabled, value, showMenu, menuCoords, menuDirection],
	);

	useEffect(() => {
		if (transitionEnded) {
			resetMenuCoords();
		}
	}, [transitionEnded]);

	return (
		<div className={
			cn(
				styles['dropdown'],
				'dropdown',
				{[styles['disabled']]: disabled}
			)} ref={handleRefSet}>
			<DropdownContext value={providerValue}>{children}</DropdownContext>
		</div>
	);
};

Dropdown.Trigger = DropdownTrigger;
Dropdown.Menu = DropdownMenu;

export default Dropdown;
